import {
  FEATURE_STATUS_COMPLETED,
  FEATURE_STATUS_FAILED,
  FEATURE_STATUS_PENDING,
  FEATURE_STATUS_RERUNNING,
} from "config";

/** All AI feature keys the backend tracks in project feature statuses. */
export const AI_RUN_FEATURES = [
  "script_breakdown",
  "character_breakdown",
  "coverage",
  "budget",
  "schedule",
  "tax_incentives",
] as const;

export type AiRunFeature = (typeof AI_RUN_FEATURES)[number];

export const aiRunFeatureLabel = (feature: string): string => {
  switch (feature) {
    case "script_breakdown":
      return "Script breakdown";
    case "character_breakdown":
      return "Character breakdown";
    case "coverage":
      return "Coverage";
    case "budget":
      return "Budget";
    case "schedule":
      return "Schedule";
    case "tax_incentives":
      return "Tax incentives";
    default:
      return "AI run";
  }
};

/**
 * Project-relative panel subpath to open when a feature run finishes.
 * These must match the child routes under
 * /workspaces/:workspaceId/projects/:projectId in routes/config.tsx. There is no
 * catch-all at that level, so an unmatched subpath falls through to the global
 * 404 page instead of landing on the panel.
 */
export const aiRunFeaturePanelPath = (feature: string): string => {
  switch (feature) {
    case "character_breakdown":
      return "characters/breakdown";
    case "coverage":
      return "script/coverage";
    case "budget":
      return "budget/breakdown";
    case "schedule":
      return "schedule/stripboard";
    case "tax_incentives":
      return "budget/taxincentives";
    case "script_breakdown":
    default:
      return "script/breakdown";
  }
};

/**
 * Why a failed AI run failed, as the user should read it. The backend replaces a failed
 * run's stored error with a safe sentence before sending it (public-run-failure.ts), so it
 * is shown as is. Undefined when no reason was sent.
 */
export const aiRunFailureReason = (metadata: unknown): string | undefined => {
  const error = (metadata as { error?: unknown } | null | undefined)?.error;
  if (typeof error !== "string") return undefined;
  const reason = error.trim();
  return reason || undefined;
};

export const isActiveAiRunStatus = (status: string | undefined): boolean =>
  status === FEATURE_STATUS_RERUNNING || status === FEATURE_STATUS_PENDING;

export const isTerminalAiRunStatus = (status: string | undefined): boolean =>
  status === FEATURE_STATUS_COMPLETED || status === FEATURE_STATUS_FAILED;

export interface AiRunStatusItem {
  feature: string;
  status: string;
  metadata?: unknown;
}

export interface AiRunTransition {
  feature: string;
  from: string;
  to: string;
  metadata?: unknown;
}

/**
 * Prev-ref diffing shared by the mounted panel providers and the global
 * watcher: given the previous per-feature statuses and the fresh rows, returns
 * active -> terminal transitions and updates `prev` in place.
 */
export const detectAiRunTransitions = (
  prev: Record<string, string | undefined>,
  statuses: AiRunStatusItem[] | undefined,
  features: readonly string[] = AI_RUN_FEATURES
): AiRunTransition[] => {
  const transitions: AiRunTransition[] = [];
  if (!statuses?.length) return transitions;

  for (const feature of features) {
    const row = statuses.find((s) => s.feature === feature);
    if (!row) continue;
    const previous = prev[feature];
    if (isActiveAiRunStatus(previous) && isTerminalAiRunStatus(row.status)) {
      transitions.push({
        feature,
        from: previous as string,
        to: row.status,
        metadata: row.metadata,
      });
    }
    prev[feature] = row.status;
  }
  return transitions;
};

/**
 * Mounted panel providers (script AI hub, budget/schedule sync) register here
 * so the global watcher does not double-toast completions they already handle.
 * Module-level on purpose: registration must be visible across the tree
 * without prop-drilling through the router.
 */
const foregroundWatchers = new Map<string, number>();

const watcherKey = (projectId: string | number, feature: string) => `${projectId}:${feature}`;

export const registerForegroundAiWatcher = (
  projectId: string | number,
  features: readonly string[]
): (() => void) => {
  const keys = features.map((feature) => watcherKey(projectId, feature));
  for (const key of keys) {
    foregroundWatchers.set(key, (foregroundWatchers.get(key) ?? 0) + 1);
  }
  return () => {
    for (const key of keys) {
      const count = foregroundWatchers.get(key) ?? 0;
      if (count <= 1) {
        foregroundWatchers.delete(key);
      } else {
        foregroundWatchers.set(key, count - 1);
      }
    }
  };
};

export const hasForegroundAiWatcher = (projectId: string | number, feature: string): boolean =>
  (foregroundWatchers.get(watcherKey(projectId, feature)) ?? 0) > 0;
